/**
 * External dependencies
 */
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import z from 'zod';
import { FormProvider, useForm } from 'react-hook-form';

/**
 * Internal dependencies
 */
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@Components/Form';
import { Input } from '@Components/Input';
import { Textarea } from '@Components/TextArea';
import type { Fish } from '@Types/fish';
import { usePaymentSources } from '@Hooks/usePaymentSources';
import { useManualCharge } from '@Hooks/useManualCharge';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { useQueryClient } from '@tanstack/react-query';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { queryKeys } from '@Hooks/usePurchases';
import { useSecret } from '../../stores/secretsStore';

const formSchema = z.object({
  fishId: z.uuid(),
  description: z.string().min(1, 'Description is required.'),
  amount: z.number().min(0.01, 'Amount must be greater than $0.01.'),
  paymentOption: z.string().min(1, 'Payment method is required.'),
});

type FormSchema = z.infer<typeof formSchema>;

export default function ManualCharge({ fish }: { fish: Fish }) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAmount, setConfirmAmount] = useState<string>('');
  const client = useQueryClient();

  const restaurantGuid = useSecret('TOAST_MANUAL_CHARGE_RESTAURANT_GUID');
  const menuGuid = useSecret('TOAST_MANUAL_CHARGE_MENU');
  const menuGroupGuid = useSecret('TOAST_MANUAL_CHARGE_GROUP');
  const menuItemGuid = useSecret('TOAST_MANUAL_CHARGE_ITEM');
  const diningOptionGuid = useSecret('TOAST_MANUAL_CHARGE_DINING_OPTION_GUID');

  const { chargeCard, isCharging } = useManualCharge();

  const { paymentSources, isLoading: isLoadingPaymentMethods } =
    usePaymentSources({ fishId: fish?.id, restaurantGuid });

  // The card owner decides which Toast customer the payment intent is bound to,
  // so their Fish id travels with the selected card.
  const cardOptions = (paymentSources?.cards ?? []).flatMap((source) =>
    source.paymentMethods.map((card) => ({
      value: `${source.fishId}:${card.id}`,
      cardFishId: source.fishId,
      card,
      label: `${source.ownerName} · ${card.cardType} •••• ${card.lastFour}`,
    })),
  );

  const handleCharge = async () => {
    if (!fish || !form.getValues('paymentOption')) {
      toastError('Missing required information');
      return;
    }

    const formValues = form.getValues();
    const errors = form.formState.errors;

    if (Object.keys(errors).length > 0) {
      toastError('Please fill in all required fields');
      return;
    }

    const selectedOption = cardOptions.find(
      (option) => option.value === formValues.paymentOption,
    );

    if (!selectedOption) {
      toastError('Selected card not found');
      return;
    }

    const { memberProfile } = fish;

    if (!memberProfile) {
      toastError('Member profile not found');
      return;
    }

    try {
      await chargeCard({
        fishDetails: {
          fName: memberProfile.fName,
          lName: memberProfile.lName,
          email: memberProfile.email!,
          phoneNumber: fish.phoneNumber,
        },
        selectedCard: selectedOption.card,
        cardFishId: selectedOption.cardFishId,
        amount: formValues.amount,
        description: formValues.description,
        restaurantGuid,
        menuGuid: menuGuid,
        menuGroupGuid: menuGroupGuid,
        menuItemGuid: menuItemGuid,
        diningOptionGuid,
      });

      toastSuccess('Charge successful!');
      form.reset({
        fishId: fish.id,
        paymentOption: formValues.paymentOption,
      });
      setConfirmAmount('');
      client.invalidateQueries({ queryKey: ['activity', fish.id] });
      client.invalidateQueries({ queryKey: queryKeys.lists() });
      setIsOpen(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to process charge';
      toastError(errorMessage);
    }
  };

  const confirm = async () => {
    form.clearErrors();

    const isValid = await form.trigger();

    if (!isValid) {
      return false;
    }

    setIsOpen(true);
  };

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fishId: fish.id,
      description: '',
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-4 md:flex-row">
        <Card className="w-full max-w-full">
          <CardContent className="flex flex-col gap-2">
            <FormProvider {...form}>
              <Form>
                <FormField
                  control={form.control}
                  name="description"
                  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
                  render={({ field: { ref, ...field } }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        {...field}
                        placeholder="Description"
                        className="h-24"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="amount"
                  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
                  render={({ field: { ref, ...field } }) => (
                    <FormItem className="w-full min-w-[200px] lg:w-2/3">
                      <FormLabel>Amount</FormLabel>
                      <Input
                        {...field}
                        step={0.01}
                        type="number"
                        value={field.value || ''}
                        placeholder="Amount"
                        onChange={(e) => {
                          field.onChange(
                            Number.parseFloat(e.currentTarget.value),
                          );
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="paymentOption"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Method</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                        disabled={!fish || cardOptions.length === 0}
                      >
                        <SelectTrigger className="w-full min-w-[200px] lg:w-2/3">
                          <SelectValue placeholder="Select a card..." />
                        </SelectTrigger>
                        <SelectContent>
                          {cardOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fish && cardOptions.length === 0 && (
                        <p className="text-muted-foreground mt-1 text-sm">
                          {isLoadingPaymentMethods
                            ? 'Loading payment methods...'
                            : 'No payment methods available'}
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => {
                      form.clearErrors();
                      form.reset();
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={confirm}
                    size="lg"
                    className="flex items-center gap-2"
                  >
                    Confirm charge
                  </Button>
                </div>
              </Form>
            </FormProvider>
          </CardContent>
        </Card>
      </div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Charge</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            You are about to charge{' '}
            <strong className="font-bold">
              {fish.memberProfile.fName} {fish.memberProfile.lName}
            </strong>{' '}
            <span className="font-bold text-red-500">
              {Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(form.getValues('amount'))}
            </span>
          </DialogDescription>
          <div className="flex flex-col gap-2">
            <div>
              <label>Reason for charge:</label>
              <p>{form.getValues('description')}</p>
            </div>
            <div>
              <label>
                To confirm, please type{' '}
                <span className="text-red-500">Confirm</span>:
              </label>
              <Input
                value={confirmAmount}
                placeholder="Confirm"
                onChange={(e) => {
                  setConfirmAmount(e.currentTarget.value);
                }}
              />
            </div>
          </div>
          <DialogFooter className="flex justify-end">
            <DialogClose asChild>
              <Button variant="secondary" size="lg">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={() => {
                handleCharge();
              }}
              disabled={
                isCharging ||
                'confirm' !== confirmAmount.trim().toLocaleLowerCase()
              }
              size="lg"
              className="flex items-center gap-2"
            >
              {isCharging ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
